import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService, PublicAuthConfig } from '../services/authService';
import AssociationLogo from '../components/AssociationLogo';
import { branding } from '../config/branding';

const Login: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState<PublicAuthConfig | null>(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    authService
      .getPublicConfig()
      .then(setAuthConfig)
      .catch(() => {
        setAuthConfig({ allow_public_register: true, registration_mode: 'open' });
      });
  }, []);

  const isClaimOnly = authConfig?.registration_mode === 'claim_only';

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    const parts = [];
    if (digits.length > 3) {
      parts.push(digits.slice(0, 3));
      if (digits.length > 6) {
        parts.push(digits.slice(3, 6));
        parts.push(digits.slice(6, 9));
        if (digits.length > 9) {
          return `${parts[0]}.${parts[1]}.${parts[2]}-${digits.slice(9, 11)}`;
        }
        return `${parts[0]}.${parts[1]}.${parts[2]}`;
      }
      return `${parts[0]}.${digits.slice(3)}`;
    }
    return digits;
  };

  const validatePasswordStrength = (value: string) => {
    if (value.length < 8) return 'A senha deve ter pelo menos 8 caracteres';
    if (!/[A-Z]/.test(value)) return 'A senha deve conter ao menos uma letra maiúscula';
    if (!/[a-z]/.test(value)) return 'A senha deve conter ao menos uma letra minúscula';
    if (!/\d/.test(value)) return 'A senha deve conter ao menos um número';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else if (mode === 'register') {
        if (password !== confirmarSenha) {
          setError('As senhas não conferem');
          setLoading(false);
          return;
        }
        const passwordError = validatePasswordStrength(password);
        if (passwordError) {
          setError(passwordError);
          setLoading(false);
          return;
        }
        await register({
          nome_completo: isClaimOnly ? undefined : nomeCompleto,
          cpf,
          email,
          senha: password,
        });
        navigate('/dashboard');
      } else {
        await authService.requestPasswordReset(email);
        setSuccess('Se o email existir, enviaremos as instruções de redefinição');
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message =
        err.response?.status === 429
          ? 'Muitas tentativas. Aguarde um minuto e tente novamente.'
          : typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((item: { msg?: string }) => item.msg).filter(Boolean).join(' ')
            : err.code === 'ECONNABORTED'
              ? 'Tempo esgotado ao conectar na API. Verifique se o backend está rodando na porta 8000.'
              : err.message === 'Network Error' || !err.response
                ? 'Não foi possível comunicar com a API. Confirme que o backend está ativo e reinicie o frontend (npm start).'
                : 'Erro ao processar';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        background: `linear-gradient(160deg, ${branding.colors.primaryDark} 0%, ${branding.colors.primary} 45%, ${branding.colors.primaryLight} 100%)`,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <AssociationLogo size={72} variant="stacked" expandOnClick />
          </Box>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
            {mode === 'login'
              ? 'Faça login para continuar'
              : mode === 'register'
                ? isClaimOnly
                  ? 'Ative seu acesso com o CPF cadastrado pela associação'
                  : 'Crie seu cadastro para acessar'
                : 'Informe seu email para redefinir a senha'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {mode === 'register' && (
              <>
                {!isClaimOnly && (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Nome completo"
                    autoComplete="name"
                    value={nomeCompleto}
                    onChange={(e) => setNomeCompleto(e.target.value)}
                  />
                )}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="CPF"
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                />
              </>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {mode !== 'forgot' && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Senha"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {mode === 'register' && (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmar-senha"
                    label="Confirmar senha"
                    type="password"
                    id="confirmar-senha"
                    value={confirmarSenha}
                    onChange={(e) => setConfirmarSenha(e.target.value)}
                  />
                )}
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.25, fontWeight: 700 }}
              disabled={loading}
            >
              {loading
                ? mode === 'login'
                  ? 'Entrando...'
                  : mode === 'register'
                    ? isClaimOnly
                      ? 'Ativando...'
                      : 'Criando...'
                    : 'Enviando...'
                : mode === 'login'
                  ? 'Entrar'
                  : mode === 'register'
                    ? isClaimOnly
                      ? 'Ativar acesso'
                      : 'Criar conta'
                    : 'Enviar link'}
            </Button>
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            gap={1}
            flexDirection={{ xs: 'column', sm: 'row' }}
            flexWrap="wrap"
          >
            <Button
              variant="text"
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => {
                setError('');
                setSuccess('');
                setMode(mode === 'login' ? 'register' : 'login');
              }}
            >
              {mode === 'login'
                ? isClaimOnly
                  ? 'Ativar meu acesso (CPF da associação)'
                  : 'Ainda não tenho cadastro'
                : 'Já tenho cadastro'}
            </Button>
            {mode !== 'forgot' && (
              <Button
                variant="text"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setError('');
                  setSuccess('');
                  setMode('forgot');
                }}
              >
                Esqueci minha senha
              </Button>
            )}
            {mode === 'forgot' && (
              <Button
                variant="text"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setError('');
                  setSuccess('');
                  setMode('login');
                }}
              >
                Voltar ao login
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;

